import React, { useState, useRef, useEffect, useCallback } from "react";
import { useField } from "formik";
import Flag from "react-flagkit";
import { motion } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import * as Popover from '@radix-ui/react-popover';
import { getCountries } from "../../../../utils/country.utils";

const CountryInput = ({ label, name, className, error, touched, placeholder, disabled = false, returnedValue = 'iso' }) => {
    const countries = getCountries();
    const [field, , helpers] = useField(name);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    
    const searchInputRef = useRef(null);

    // Assurer que field.value n'est jamais null
    const fieldValue = field.value || "";
    const selectedCountry = countries.find((country) => country[returnedValue] === fieldValue);

    const filteredCountries = countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Gestion de la sélection d'un pays
    const handleCountrySelect = useCallback((country) => {
        if (!disabled) {
            helpers.setValue(country || "");
            setOpen(false);
            setSearchTerm("");
            setHighlightedIndex(-1);
        }
    }, [disabled, helpers]);

    // Gestion des touches clavier pour la navigation dans la liste
    const handleKeyDown = useCallback((e) => {
        if (disabled || !open) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredCountries.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : filteredCountries.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
                    handleCountrySelect(filteredCountries[highlightedIndex][returnedValue]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                setSearchTerm("");
                setHighlightedIndex(-1);
                break;
        }
    }, [disabled, open, highlightedIndex, filteredCountries, handleCountrySelect, returnedValue]);

    // Focus sur l'input de recherche quand le popover s'ouvre
    useEffect(() => {
        if (open && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [open]);

    // Reset highlighted index when filtered countries change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [searchTerm]);

    return (
        <div className={`${className} max-w-xs`}>
            {label && (
                <label 
                    htmlFor={`country-input-${name}`}
                    className="block text-sm font-medium mb-2 text-base-content"
                >
                    {label}
                </label>
            )}
            
            <Popover.Root open={open} onOpenChange={setOpen}>
                <Popover.Trigger asChild>
                    <button
                        type="button"
                        id={`country-input-${name}`}
                        className={`
                            input input-primary min-h-[44px] flex items-center justify-between w-full px-4 py-3
                            transition-all duration-200 cursor-pointer
                            ${open ? 'input-focus' : ''}
                            ${disabled ? 'input-disabled cursor-not-allowed' : 'hover:shadow-md'}
                        `}
                        disabled={disabled}
                        aria-label={`Sélectionner un pays. Actuellement sélectionné: ${selectedCountry?.name || 'Aucun pays sélectionné'}`}
                    >
                        <div className="flex items-center gap-3">
                            <Flag 
                                country={selectedCountry?.iso || "FR"} 
                                size={24} 
                                className="flex-shrink-0 rounded-sm shadow-sm" 
                            />
                            <span className="text-base text-base-content truncate text-left">
                                {selectedCountry?.name || placeholder || "Choisir un pays"}
                            </span>
                        </div>
                        <motion.div
                            animate={{ rotate: open ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex-shrink-0 ml-2"
                        >
                            <ChevronDownIcon className="h-5 w-5 text-base-content/60" />
                        </motion.div>
                    </button>
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Content
                        className="z-[10000] w-[var(--radix-popover-trigger-width)] bg-base-100 border-2 border-base-300 
                                 rounded-lg shadow-xl overflow-hidden max-h-[300px]"
                        sideOffset={8}
                        side="bottom"
                        align="start"
                        collisionPadding={20}
                        onKeyDown={handleKeyDown}
                    >
                        {/* Search Input */}
                        <div className="sticky top-0 bg-base-100 border-b border-base-300 p-3">
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Rechercher un pays..."
                                className="input input-primary w-full text-base"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={disabled}
                                aria-label="Rechercher un pays"
                            />
                        </div>
                        
                        {/* Countries List */}
                        <div className="max-h-[200px] overflow-y-auto overflow-x-hidden">
                            {filteredCountries.length > 0 ? (
                                <div className="py-1">
                                    {filteredCountries.map((country, index) => (
                                        <motion.div
                                            key={country.iso}
                                            className={`
                                                flex items-center gap-3 px-4 py-3 cursor-pointer
                                                transition-colors duration-150 min-h-[48px]
                                                ${index === highlightedIndex 
                                                    ? 'bg-primary text-primary-content' 
                                                    : 'hover:bg-base-200 text-base-content'
                                                }
                                            `}
                                            onClick={() => handleCountrySelect(country[returnedValue])}
                                            whileHover={{ x: 4 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <Flag 
                                                country={country.iso} 
                                                size={24} 
                                                className="flex-shrink-0 rounded-sm shadow-sm" 
                                            />
                                            <span className="text-base font-medium">
                                                {country.name}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="px-4 py-8 text-center text-base-content/60">
                                    <p className="text-base">Aucun pays trouvé</p>
                                    <p className="text-sm mt-1">Essayez un autre terme de recherche</p>
                                </div>
                            )}
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>

            {/* Error Message */}
            {error && touched && (
                <motion.p 
                    className="text-error text-sm mt-2 font-medium"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    {error}
                </motion.p>
            )}
        </div>
    );
};

export default CountryInput;
